import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Terminal, Zap, Layers, Cpu, Eye, Bug, Moon, GitBranch, Puzzle, Lock, Settings } from 'lucide-react';
import { CodeBlock } from '@/components/docs/CodeBlock';

// Animated skeleton blueprint for hero
function HeroBlueprint({ className, delay = 0, style }) {
  return (
    <motion.div
      className={`rounded-sm animate-shimmer ${className}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      style={style}
    />
  );
}

// Hero skeleton preview
function HeroSkeleton() {
  return (
    <div className="relative w-full max-w-md mx-auto">
      {/* Card skeleton */}
      <div className="border border-white/[0.08] rounded-lg bg-[#0A0A0A] p-6 glow-border">
        <div className="flex items-center gap-4 mb-5">
          <HeroBlueprint className="w-14 h-14 rounded-full" delay={0.1} />
          <div className="flex-1 space-y-2.5">
            <HeroBlueprint className="h-4 w-3/4 rounded" delay={0.2} />
            <HeroBlueprint className="h-3 w-1/2 rounded" delay={0.3} />
          </div>
        </div>
        <div className="space-y-2.5">
          <HeroBlueprint className="h-3 w-full rounded" delay={0.4} />
          <HeroBlueprint className="h-3 w-5/6 rounded" delay={0.5} />
          <HeroBlueprint className="h-3 w-4/6 rounded" delay={0.6} />
        </div>
        <div className="flex gap-3 mt-6">
          <HeroBlueprint className="h-9 w-24 rounded-md" delay={0.7} />
          <HeroBlueprint className="h-9 w-20 rounded-md" delay={0.8} />
        </div>
      </div>
      {/* Floating badge */}
      <motion.div
        className="absolute -top-3 -right-3 bg-white text-black text-[10px] font-mono font-bold px-2.5 py-1 rounded-full"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: 'spring' }}
      >
        AUTO-GENERATED
      </motion.div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description, delay = 0 }) {
  return (
    <motion.div
      className="border border-white/[0.06] bg-[#0A0A0A] p-6 hover:border-white/[0.12] transition-all group"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
    >
      <Icon className="w-5 h-5 text-white/40 mb-4 group-hover:text-white/70 transition-colors" />
      <h3 className="font-bold text-sm mb-2 tracking-tight">{title}</h3>
      <p className="text-[13px] text-white/40 leading-relaxed">{description}</p>
    </motion.div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden grid-bg" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div>
              <motion.div
                className="inline-flex items-center gap-2 border border-white/10 rounded-full px-3 py-1.5 mb-8"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-mono text-white/50">v1.0 just shipped</span>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                data-testid="hero-title"
              >
                Skeleton loaders,
                <br />
                <span className="text-white/40">auto-generated.</span>
              </motion.h1>

              <motion.p
                className="text-base text-white/40 max-w-md leading-relaxed mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                GhostLayout analyzes your actual UI and generates pixel-perfect skeleton loaders automatically. No more hand-coding placeholder divs.
              </motion.p>

              <motion.div
                className="flex flex-wrap gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Link
                  to="/docs"
                  className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-gray-200 transition-colors"
                  data-testid="hero-get-started-btn"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <div className="flex items-center gap-2 border border-white/10 rounded-md px-4 py-2.5">
                  <Terminal className="w-4 h-4 text-white/30" />
                  <code className="text-sm font-mono text-white/60">npm i @ghostlayout/react</code>
                </div>
              </motion.div>
            </div>

            {/* Right: Skeleton demo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <HeroSkeleton />
            </motion.div>
          </div>
        </div>

        {/* Gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="border-t border-white/[0.06] py-24" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div className="text-center mb-16" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">Three steps. Five minutes.</h2>
            <p className="text-sm text-white/40 max-w-lg mx-auto">Wrap your components, run the CLI, import the registry. That's it.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0 }}>
              <div className="text-[10px] font-mono text-white/20 tracking-[0.2em] uppercase mb-3">Step 01</div>
              <h3 className="font-bold text-lg mb-3 tracking-tight">Wrap components</h3>
              <CodeBlock
                filename="page.tsx"
                code={`<Skeleton name="card" loading={isLoading}>
  <Card data={data} />
</Skeleton>`}
              />
            </motion.div>

            {/* Step 2 */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <div className="text-[10px] font-mono text-white/20 tracking-[0.2em] uppercase mb-3">Step 02</div>
              <h3 className="font-bold text-lg mb-3 tracking-tight">Run the CLI</h3>
              <CodeBlock
                language="bash"
                code={`npx ghostlayout build

# Auto-detects your dev server
# Captures at 375px, 768px, 1024px, 1440px
# Generates *.blueprints.json + registry.js`}
              />
            </motion.div>

            {/* Step 3 */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <div className="text-[10px] font-mono text-white/20 tracking-[0.2em] uppercase mb-3">Step 03</div>
              <h3 className="font-bold text-lg mb-3 tracking-tight">Import registry</h3>
              <CodeBlock
                filename="layout.tsx"
              code={`import './blueprints/registry'

// That's it. Every <Skeleton>
// auto-resolves its blueprints by name.`}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES BENTO ===== */}
      <section className="border-t border-white/[0.06] py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div className="mb-16" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">Built for real-world apps</h2>
            <p className="text-sm text-white/40 max-w-lg">Everything you need to ship beautiful loading states.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.03] border border-white/[0.06] rounded-lg overflow-hidden">
            <FeatureCard icon={Zap} title="Zero Manual Work" description="CLI extracts layout from your real UI. No placeholder code needed." delay={0} />
            <FeatureCard icon={Layers} title="Pixel Perfect" description="Uses getBoundingClientRect for exact positions, sizes, and radii." delay={0.05} />
            <FeatureCard icon={GitBranch} title="Multi-Breakpoint" description="Responsive skeletons for 375px, 768px, 1024px, and 1440px." delay={0.1} />
            <FeatureCard icon={Cpu} title="High Performance" description="Pre-generated JSON. Memoized rendering. Zero runtime reflows." delay={0.15} />
            <FeatureCard icon={Moon} title="Dark Mode" description="Automatic color adjustment for light and dark themes." delay={0.2} />
            <FeatureCard icon={Bug} title="Debug Overlay" description="Visual inspection with bone outlines, count, and viewport info." delay={0.25} />
            <FeatureCard icon={Puzzle} title="Fixture Prop" description="Mock data for CLI capture. Works with auth-gated components." delay={0.3} />
            <FeatureCard icon={Lock} title="Auth Support" description="Cookies and headers config for protected pages." delay={0.35} />
            <FeatureCard icon={Settings} title="Config File" description="ghostlayout.config.json for project-wide defaults." delay={0.4} />
          </div>
        </div>
      </section>

      {/* ===== OUTPUT PREVIEW ===== */}
      <section className="border-t border-white/[0.06] py-24" data-testid="output-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div className="mb-12" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">What gets generated</h2>
            <p className="text-sm text-white/40 max-w-lg">Lightweight JSON. One file per component. Auto-registered.</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-6">
            <CodeBlock
              filename="blueprints/user-card.blueprints.json"
              language="json"
              code={`{
  "breakpoints": {
    "375": {
      "blueprints": [
        { "x": 6, "y": 24, "w": 20, "h": 56, "r": 28 },
        { "x": 32, "y": 24, "w": 50, "h": 22, "r": 0 },
        { "x": 32, "y": 50, "w": 35, "h": 16, "r": 0 },
        { "x": 6, "y": 92, "w": 40, "h": 14, "r": 0 },
        { "x": 6, "y": 146, "w": 88, "h": 40, "r": 4 }
      ]
    },
    "768": { "blueprints": [...] },
    "1024": { "blueprints": [...] },
    "1440": { "blueprints": [...] }
  }
}`}
            />
            <CodeBlock
              filename="blueprints/registry.js"
              language="js"
              code={`// Auto-generated by GhostLayout CLI
import { registerBlueprints } from '@ghostlayout/react'

const userCardData = { /* ... */ }
const productCardData = { /* ... */ }
const dashboardData = { /* ... */ }

registerBlueprints({
  "user-card": userCardData,
  "product-card": productCardData,
  "dashboard": dashboardData
})`}
            />
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="border-t border-white/[0.06] py-24" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">Start building</h2>
            <p className="text-sm text-white/40 max-w-md mx-auto mb-8">Add skeleton loading screens to any React app in under 5 minutes.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/docs/installation"
                className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-gray-200 transition-colors"
                data-testid="cta-install-btn"
              >
                Installation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/playground"
                className="inline-flex items-center gap-2 border border-white/10 px-5 py-2.5 rounded-md text-sm text-white/60 hover:text-white hover:border-white/20 transition-colors"
                data-testid="cta-playground-btn"
              >
                <Eye className="w-4 h-4" /> Try Playground
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-white/[0.06] py-8" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <p className="text-xs text-white/20 font-mono">GhostLayout v1.0</p>
          <p className="text-xs text-white/20">MIT License</p>
        </div>
      </footer>
    </div>
  );
}
